"use client";

// The IPO AV disclaimer (Northstar legal text), shown as a blocking dialog
// when a reader asks to view or download the DRHP audio-visual film in either
// language — NOT on page load. Confirming they are located in India releases
// the pending video; declining swaps the dialog for the restricted message and
// the video stays closed. Escape and backdrop clicks deliberately do nothing.
//
// This is a separate dialog from InvestorDisclaimerGate on purpose: the AV
// carries its own legal text (India-residency confirmation, SEBI master
// circular reference) and its own acknowledgement cookie, so confirming one
// never unlocks the other.
//
// Copy lives in lib/investorAvDisclaimer.ts and every line is overridable
// from /admin/pages → Investor Relations through the `av.*` keys. Because the
// dialog only exists after a click, the admin preview opens it for in-place
// editing only when the preview URL carries gate=av (the offer-document
// disclaimer answers to gate=doc; the plain edit preview opens neither, so
// the page itself stays editable).
import { useEffect, useState } from "react";
import {
  AV_TITLE,
  AV_LEAD_PARAS,
  AV_IMPORTANT,
  AV_PARAS,
  AV_CONFIRM_PARAS,
  AV_CONFIRM_LABEL,
  AV_DECLINE_LABEL,
  AV_BLOCKED,
} from "@/lib/investorAvDisclaimer";

export function InvestorAvDisclaimerGate({
  open,
  onConfirm,
  onClose,
  c = {},
}: {
  /** Render the dialog (a gated AV click is pending). */
  open: boolean;
  /** Reader confirmed they are located in India — release the pending video. */
  onConfirm: () => void;
  /** Dialog dismissed without confirming (after declining). */
  onClose: () => void;
  c?: Record<string, string>;
}) {
  const [declined, setDeclined] = useState(false);
  const [editing, setEditing] = useState(false);
  const t = (key: string, fallback: string) => c[key]?.trim() || fallback;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setEditing(
      params.has("tsedit") && params.get("gate") === "av" && window.parent !== window,
    );
  }, []);

  const show = open || editing;

  // Ask afresh on every open.
  useEffect(() => {
    if (open) setDeclined(false);
  }, [open]);

  // Hold the page still while the dialog is up (html too — iOS Safari ignores
  // overflow:hidden on the body alone).
  useEffect(() => {
    if (!show) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [show]);

  if (!show) return null;

  const leads = AV_LEAD_PARAS.map((p, i) => t(`av.lead.${i}`, p));
  const paras = AV_PARAS.map((p, i) => t(`av.p.${i}`, p));
  const confirmParas = AV_CONFIRM_PARAS.map((p, i) => t(`av.confirm.${i}`, p));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="investor-av-disclaimer-title"
      className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-brand-dark/60 p-4 py-8 backdrop-blur-[2px] md:p-8"
    >
      {declined ? (
        <div className="w-full max-w-xl rounded-2xl bg-white p-8 text-center shadow-2xl md:p-12">
          <p className="mono-meta uppercase text-brand-accent-dark">Access restricted</p>
          <p className="mt-5 text-lg leading-relaxed text-brand-ink md:text-xl" data-cms-key="av.blocked">
            {t("av.blocked", AV_BLOCKED)}
          </p>
          <button type="button" onClick={onClose} className="btn-secondary btn-sm mt-8 inline-flex">
            Close
          </button>
        </div>
      ) : (
        <div className="flex max-h-[calc(100dvh-4rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="border-b border-brand-line px-6 py-5 md:px-10 md:py-6">
            <h2
              id="investor-av-disclaimer-title"
              className="t-h3 text-brand-ink"
              data-cms-key="av.title"
            >
              {t("av.title", AV_TITLE)}
            </h2>
          </div>

          {/* the disclaimer itself — scrolls inside the dialog */}
          <div className="grow overflow-y-auto overscroll-contain px-6 py-6 md:px-10 md:py-8">
            <div className="space-y-4">
              {leads.map((p, i) => (
                <p key={i} className="text-xs font-medium uppercase leading-relaxed tracking-wide text-brand-ink" data-cms-key={`av.lead.${i}`}>
                  {p}
                </p>
              ))}
            </div>
            <p className="mt-6 text-sm font-medium leading-relaxed text-brand-ink" data-cms-key="av.important">
              {t("av.important", AV_IMPORTANT)}
            </p>
            <div className="mt-6 space-y-5">
              {paras.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed text-brand-slate" data-cms-key={`av.p.${i}`}>
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-8 space-y-5 border-t border-brand-line pt-8">
              {confirmParas.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed text-brand-slate" data-cms-key={`av.confirm.${i}`}>
                  {p}
                </p>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-brand-line bg-brand-surface px-6 py-5 sm:flex-row sm:justify-end md:px-10">
            <button
              type="button"
              onClick={(e) => {
                if (editing && (e.target as HTMLElement).closest("[data-cms-key]")) return;
                setDeclined(true);
              }}
              className="btn-secondary btn-sm justify-center"
            >
              <span data-cms-key="av.decline.label">
                {t("av.decline.label", AV_DECLINE_LABEL)}
              </span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                if (editing && (e.target as HTMLElement).closest("[data-cms-key]")) return;
                onConfirm();
              }}
              className="btn-primary btn-sm justify-center"
            >
              <span data-cms-key="av.confirm.label">
                {t("av.confirm.label", AV_CONFIRM_LABEL)}
              </span>
            </button>
          </div>

          {editing && (
            <div className="border-t border-brand-line bg-brand-veil px-6 py-5 md:px-10">
              <p className="mono-meta uppercase text-brand-muted">
                Shown after &ldquo;{t("av.decline.label", AV_DECLINE_LABEL)}&rdquo; — editable here
              </p>
              <p className="mt-2 text-sm leading-relaxed text-brand-ink" data-cms-key="av.blocked">
                {t("av.blocked", AV_BLOCKED)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
